import { LinkItem } from '@occc/ui-design-system';
import { OfferType } from '@occc-app/offerValidationService/types';
import { useGetCampaignById } from '@occc-app/dataAccessLayer/campaign/queries';

import { CampaignComponentTabs } from './components/ConfigurationLayout';

import { routeCreator as campaignRouteCreator } from '../Campaign/utils';

export const baseRouteCreator = {
  details: (campaignId: string, campaignComponentId: string) =>
    `/campaigns/${campaignId}/${campaignComponentId}`,
  createWithPrefix: (prefix: string) => `${prefix}/create`,
  configureWithPrefix: (prefix: string, campaignComponentId: string) =>
    `${prefix}/${campaignComponentId}/configure/*`,
  updateWithPrefix: (prefix: string, campaignComponentId: string) =>
    `${prefix}/${campaignComponentId}/update/*`,
};

export const getUpdatePageLinks = ({
  campaignId,
  campaignComponentId,
  campaignName,
  campaignComponentName,
  pageTitle,
}: {
  campaignId: string;
  campaignComponentId: string;
  campaignName: string;
  campaignComponentName: string;
  pageTitle: string;
}): LinkItem[] => {
  return [
    {
      to: campaignRouteCreator.list(),
      label: 'Campaigns',
    },
    {
      to: campaignRouteCreator.details(campaignId),
      label: campaignName,
    },
    {
      to: baseRouteCreator.details(campaignId, campaignComponentId),
      label: campaignComponentName || 'Loading...',
    },
    {
      key: pageTitle,
      label: pageTitle,
    },
  ];
};

export const getCreatePageLinks = (
  campaignId: string,
  campaignName: string,
  lastRouteLabel: string,
): LinkItem[] => {
  return [
    {
      to: campaignRouteCreator.list(),
      label: 'Campaigns',
    },
    {
      to: campaignRouteCreator.details(campaignId),
      label: campaignName,
    },
    {
      key: `/campaigns/${campaignId}/component/create`,
      label: lastRouteLabel,
    },
  ];
};

export const createRouteCreator = (offerType: OfferType) => {
  const routeCreator = {
    base: (campaignId: string) => `/campaigns/${campaignId}/${offerType}`,
    create: (campaignId: string) => `${routeCreator.base(campaignId)}/create`,
    configure: (campaignId: string, campaignComponentId: string) =>
      `${routeCreator.base(campaignId)}/${campaignComponentId}/configure`,
    configureWithTab: (
      campaignId: string,
      campaignComponentId: string,
      tab: CampaignComponentTabs = CampaignComponentTabs.Configure,
    ) => `${routeCreator.configure(campaignId, campaignComponentId)}/${tab}`,
    update: (campaignId: string, campaignComponentId: string) =>
      `${routeCreator.base(campaignId)}/${campaignComponentId}/update`,
    updateWithTab: (
      campaignId: string,
      campaignComponentId: string,
      tab: CampaignComponentTabs = CampaignComponentTabs.Configure,
    ) => `${routeCreator.update(campaignId, campaignComponentId)}/${tab}`,
    ...baseRouteCreator,
  };

  return routeCreator;
};
export const errorLabel = { key: 'error', label: 'Something went wrong' };
export const notFoundLabel = { key: 'not-found', label: 'Page Not Found' };
export const loadingLabel = { key: 'loading', label: 'Loading...' };

type GetDetailsPageLinks = (
  loading: boolean,
  error: boolean,
  campaignId?: string,
  campaignName?: string,
  offerId?: string,
  offerName?: string,
) => LinkItem[];

export const getDetailsPageLinks: GetDetailsPageLinks = (
  loading: boolean,
  error: boolean,
  campaignId,
  campaignName,
  offerId,
  offerName,
): LinkItem[] => {
  const links: LinkItem[] = [
    {
      to: campaignRouteCreator.list(),
      label: 'Campaigns',
    },
  ];

  if (error) {
    return [...links, errorLabel];
  }

  if (campaignName && campaignId) {
    links.push({
      to: campaignRouteCreator.details(campaignId),
      label: campaignName,
    });
  } else if (!loading) {
    return [...links, notFoundLabel];
  }

  if (offerName && offerId) {
    return [
      ...links,
      {
        key: `/campaigns/${campaignId}/${offerId}`,
        label: offerName,
      },
    ];
  }

  return [...links, loading ? loadingLabel : notFoundLabel];
};

// TODO: can we move this one to the plase of usage?
export const useGetCampaignName = (chainId: string, campaignId: string) => {
  const { loading, error, data: campaign } = useGetCampaignById(campaignId);

  if (error) {
    return '';
  }

  if (!campaign && loading) {
    return 'Loading...';
  }

  return campaign?.name || '';
};
